import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { workflowAutomationService } from '../../../services/workflowAutomationService';

const WorkflowAutomationModal = ({ isOpen, onClose, contracts = [] }) => {
  const [automationType, setAutomationType] = useState('');
  const [selectedContracts, setSelectedContracts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [configuration, setConfiguration] = useState({
    enableAIReview: true,
    autoApprove: false,
    exportToPDF: true,
    daysAhead: 7
  });

  const automationTypeOptions = [
    { value: 'batch_processing', label: 'معالجة دفعة العقود' },
    { value: 'bulk_documents', label: 'إنشاء مستندات متعددة' },
    { value: 'auto_followup', label: 'جدولة متابعات تلقائية' }
  ];

  const handleContractToggle = (contractId) => {
    setSelectedContracts(prev =>
      prev?.includes(contractId)
        ? prev?.filter(id => id !== contractId)
        : [...prev, contractId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContracts?.length === contracts?.length) {
      setSelectedContracts([]);
    } else {
      setSelectedContracts(contracts?.map(c => c?.id));
    }
  };

  const handleStartAutomation = async () => {
    if (!automationType || selectedContracts?.length === 0) {
      alert('الرجاء اختيار نوع الأتمتة والعقود');
      return;
    }

    setIsProcessing(true);

    try {
      let result;

      if (automationType === 'batch_processing') {
        result = await workflowAutomationService?.processBatchContracts(
          selectedContracts,
          {
            enableAIReview: configuration?.enableAIReview,
            autoApprove: configuration?.autoApprove
          }
        );
        alert(`تمت معالجة ${result?.totalProcessed} عقد بنجاح!`);
      } else if (automationType === 'bulk_documents') {
        const items = contracts?.filter(c => selectedContracts?.includes(c?.id))?.map(c => ({
          id: c?.id,
          clientName: c?.clientName,
          clientId: c?.clientId,
          clientPhone: c?.clientPhone,
          clientEmail: c?.clientEmail,
          propertyTitle: c?.propertyTitle,
          propertyType: c?.propertyType,
          propertyAddress: c?.location,
          targetPrice: c?.price
        }));

        result = await workflowAutomationService?.generateBulkDocuments(
          items,
          'marketing_contract',
          { exportToPDF: configuration?.exportToPDF }
        );
        alert(`تم إنشاء ${result?.totalGenerated} مستند بنجاح!`);
      } else if (automationType === 'auto_followup') {
        result = await workflowAutomationService?.scheduleAutomatedFollowUps({
          daysAhead: configuration?.daysAhead
        });
        alert(`تم جدولة ${result?.totalScheduled} متابعة بنجاح!`);
      }

      onClose();
    } catch (error) {
      console.error('Workflow automation error:', error);
      
      // Show user-friendly error message
      const errorMessage = error?.message || 'حدث خطأ أثناء تنفيذ العملية';
      alert(`خطأ: ${errorMessage}`);
      
      setIsProcessing(false);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="أتمتة سير العمل"
      size="lg"
      footer={null}
      icon="Workflow"
      iconColor="var(--color-primary)"
    >
      <div className="space-y-6" dir="rtl">
        {/* Automation Type Selection */}
        <div>
          <Select
            label="نوع الأتمتة"
            options={automationTypeOptions}
            value={automationType}
            onChange={setAutomationType}
            placeholder="اختر نوع الأتمتة"
            required
          />
        </div>

        {/* Configuration Options */}
        {automationType && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              إعدادات الأتمتة
            </h3>

            {automationType === 'batch_processing' && (
              <>
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={configuration?.enableAIReview}
                    onChange={(checked) => setConfiguration(prev => ({ ...prev, enableAIReview: checked }))}
                  />
                  <label className="text-sm text-foreground">
                    تفعيل المراجعة بالذكاء الاصطناعي
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={configuration?.autoApprove}
                    onChange={(checked) => setConfiguration(prev => ({ ...prev, autoApprove: checked }))}
                  />
                  <label className="text-sm text-foreground">
                    الموافقة التلقائية (للعقود المطابقة 80%+)
                  </label>
                </div>
              </>
            )}

            {automationType === 'bulk_documents' && (
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={configuration?.exportToPDF}
                  onChange={(checked) => setConfiguration(prev => ({ ...prev, exportToPDF: checked }))}
                />
                <label className="text-sm text-foreground">
                  تصدير إلى PDF تلقائياً
                </label>
              </div>
            )}

            {automationType === 'auto_followup' && (
              <div>
                <Input
                  label="عدد الأيام للمتابعة"
                  type="number"
                  value={configuration?.daysAhead}
                  onChange={(e) => setConfiguration(prev => ({ ...prev, daysAhead: parseInt(e?.target?.value) || 7 }))}
                  placeholder="7"
                  min="1"
                  max="30"
                />
              </div>
            )}
          </div>
        )}

        {/* Contract Selection */}
        {automationType && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-heading font-semibold text-foreground">
                اختر العقود ({selectedContracts?.length} محدد)
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
              >
                {selectedContracts?.length === contracts?.length ? 'إلغاء الكل' : 'تحديد الكل'}
              </Button>
            </div>

            <div className="space-y-2 max-h-64 overflow-auto border border-border rounded-lg p-3">
              {contracts?.map((contract) => (
                <div
                  key={contract?.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Checkbox
                      checked={selectedContracts?.includes(contract?.id)}
                      onChange={() => handleContractToggle(contract?.id)}
                    />
                    <div className="text-right">
                      <p className="text-sm font-medium text-foreground">
                        {contract?.contractNumber} - {contract?.propertyTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {contract?.clientName} • {contract?.statusLabel}
                      </p>
                    </div>
                  </div>
                  <Icon name="FileText" size={20} color="var(--color-muted-foreground)" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Box */}
        {automationType && (
          <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
              <div className="text-right">
                <p className="text-sm font-medium text-foreground mb-1">
                  مدعوم بالذكاء الاصطناعي
                </p>
                <p className="text-xs text-muted-foreground">
                  {automationType === 'batch_processing' && 'سيتم مراجعة العقود تلقائياً باستخدام Anthropic Claude للتحقق من الامتثال القانوني.'}
                  {automationType === 'bulk_documents' && 'سيتم إنشاء المستندات تلقائياً باستخدام OpenAI GPT-4 مع قوالب احترافية.'}
                  {automationType === 'auto_followup' && 'سيتم جدولة المتابعات تلقائياً مع رسائل مخصصة باستخدام الذكاء الاصطناعي.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-start gap-3 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
          >
            إلغاء
          </Button>
          <Button
            variant="default"
            iconName="Play"
            iconPosition="right"
            onClick={handleStartAutomation}
            disabled={!automationType || selectedContracts?.length === 0 || isProcessing}
          >
            {isProcessing ? 'جاري المعالجة...' : `بدء الأتمتة (${selectedContracts?.length})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default WorkflowAutomationModal;